import { createSelector } from '@reduxjs/toolkit';
import * as fromContext from 'app/reducers/context.reducer';
import { AuthState, TokenInfo } from 'app/reducers/context.reducer';
import { ADMINISTRATOR, SUPER_MANAGER, MANAGER, SUPPORT, USER, VERSIONS_EDITOR_EMAIL_WHITELIST, } from "app/config";
import { createFeatureSelector } from "@ngrx/store";
import { getPermissionsFromToken, isSystemAdministratorFromToken } from 'app/helpers/jwt-decoder';

export interface RoleMembership {
  isUser: boolean;
  isManager: boolean;
  isSuperManager: boolean;
  isSupport: boolean;
  isAdministrator: boolean;
  isVersionsEditor: boolean;
}

const selectSelf = createFeatureSelector<AuthState>(fromContext.AuthFeatureKey);

export const selectTokenInfo = createSelector(
  selectSelf,
  (state): TokenInfo | null => {
    // Фича 'auth' может отсутствовать в сторе (например, в юнит-тестах с provideMockStore()
    // без начального состояния) — тогда отдаём null, а не падаем на обращении к полю.
    return state?.tokenInfo ?? null;
  }
);

export const selectIsAuthenticated = createSelector(
  selectSelf,
  (state): boolean => {
    // Фича 'auth' может отсутствовать в сторе (юнит-тесты с provideMockStore()) — тогда false.
    return !!(state?.tokenInfo && state.tokenInfo.tokenExpires > new Date());
  }
);

export const selectLanguage = createSelector(
  selectSelf,
  (state) => {
    return state.language;
  }
);

export const selectLoginData = createSelector(
  selectSelf,
  (state) => {
    // Фича 'auth' может отсутствовать в сторе (юнит-тесты с provideMockStore()) — тогда null,
    // чтобы зависящие селекторы (selectRole → selectHasFullPermissionAccess) не падали.
    return state?.loginData ?? null;
  }
);

export const selectRole = createSelector(
  selectLoginData,
  (loginData): RoleMembership => {

    const isAdministrator = !!(loginData && loginData.roles.find(r => r === ADMINISTRATOR));
    const isSupport = isAdministrator || !!(loginData && loginData.roles.find(r => r === SUPPORT))
    const isSuperManager = isAdministrator || isSupport || !!(loginData && loginData.roles.find(r => r === SUPER_MANAGER))
    const isManager = isAdministrator || isSupport || isSuperManager || !!(loginData && loginData.roles.find(r => r === MANAGER))
    const isUser = isAdministrator || isSupport || isSuperManager || isManager || !!(loginData && loginData.roles.find(r => r === USER))

    const email = (loginData?.email ?? '').toLowerCase().trim();
    const isVersionsEditor = VERSIONS_EDITOR_EMAIL_WHITELIST.has(email);

    return {
      isUser,
      isSuperManager,
      isManager,
      isSupport,
      isAdministrator,
      isVersionsEditor,
    };
  }
);

export const selectCanEditVersions = createSelector(
  selectRole,
  (roles) => roles.isVersionsEditor
);

export const selectIfManager = createSelector(
  selectRole,
  (roles) => {
    return roles.isAdministrator || roles.isManager || roles.isSupport
  }
);

export const selectIfAdministrator = createSelector(
  selectRole,
  (roles) => {
    return roles.isAdministrator
  }
);

export const selectIfSupport = createSelector(
  selectRole,
  (roles) => {
    return roles.isAdministrator || roles.isSupport
  }
);

export const selectPermissions = createSelector(
  selectTokenInfo,
  (tokenInfo): number[] => {
    if (!tokenInfo?.token) {
      return [];
    }
    return getPermissionsFromToken(tokenInfo.token);
  }
);

// Системный администратор имеет все права неявно: бэк вместо списка кодов
// кладёт в токен маркер is_system_administrator.
export const selectIsSystemAdministrator = createSelector(
  selectTokenInfo,
  (tokenInfo): boolean => {
    return !!tokenInfo?.token && isSystemAdministratorFromToken(tokenInfo.token);
  }
);

// Полный доступ ко всем правам на фронте: системный администратор новой системы (маркер в токене)
// ИЛИ легаси-администратор (роль Administrator в loginData.roles). У перенесённого админа маркер есть,
// у легаси-админа — только роль; и тот и другой должны проходить любые проверки прав.
export const selectHasFullPermissionAccess = createSelector(
  selectIsSystemAdministrator,
  selectIfAdministrator,
  (isSystemAdministrator, isLegacyAdministrator): boolean => isSystemAdministrator || isLegacyAdministrator,
);

export const selectHasPermission = (permissionCode: number) => createSelector(
  selectPermissions,
  selectHasFullPermissionAccess,
  (permissions, hasFullAccess): boolean => {
    return hasFullAccess || permissions.includes(permissionCode);
  }
);

export const selectHasAnyPermission = (permissionCodes: number[]) => createSelector(
  selectPermissions,
  selectHasFullPermissionAccess,
  (permissions, hasFullAccess): boolean => {
    return hasFullAccess || permissionCodes.some(code => permissions.includes(code));
  }
);

export const selectHasAllPermissions = (permissionCodes: number[]) => createSelector(
  selectPermissions,
  selectHasFullPermissionAccess,
  (permissions, hasFullAccess): boolean => {
    return hasFullAccess || permissionCodes.every(code => permissions.includes(code));
  }
);
